import React from 'react';
import { Capacitor } from '@capacitor/core';
import { useClerk } from '../../auth/useAuth';
import { useNativeAuthOptional } from '../../auth/nativeAuth';
import { navigateToOAuthUrl } from '../../auth/oauthNavigation';
import type { NativeClerkSnapshot } from '../../auth/nativeClerkPlugin';
import { track } from '../../services/analyticsService';
import { useChanceyTheme } from '../../theme/ThemeContext';
import { Btn } from '../chancey/Btn';
import { friendlyErrorMessage } from '../../utils/errorCopy';

type OAuthStrategy = 'oauth_google' | 'oauth_apple';

const appUrl = (path: string): string =>
  typeof window !== 'undefined' ? new URL(path, window.location.origin).toString() : path;

const providerForStrategy = (strategy: OAuthStrategy): 'google' | 'apple' =>
  strategy === 'oauth_google' ? 'google' : 'apple';

const errorMessage = (err: unknown): string => friendlyErrorMessage(err, 'Sign-in failed');

const withTimeout = async <T,>(
  promise: Promise<T>,
  milliseconds: number,
  message: string
): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error(message)), milliseconds);
      }),
    ]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

const nativeUnavailableMessage = (
  nativeAuth: ReturnType<typeof useNativeAuthOptional>
): string =>
  nativeAuth?.unavailableReason ||
  'Native sign-in is unavailable on this build. Please update the app and try again.';

export const CapacitorSignIn: React.FC = () => {
  const { c, typ } = useChanceyTheme();
  const clerk = useClerk();
  const nativeAuth = useNativeAuthOptional();
  const isNative = Capacitor.isNativePlatform();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [busy, setBusy] = React.useState<null | 'google' | 'apple' | 'email'>(null);
  const [error, setError] = React.useState<string | null>(null);

  const startOAuth = async (strategy: OAuthStrategy) => {
    if (!isNative && !clerk?.client?.signIn) return;
    setError(null);
    setBusy(providerForStrategy(strategy));
    try {
      if (isNative) {
        if (!nativeAuth?.isAvailable) {
          throw new Error(nativeUnavailableMessage(nativeAuth));
        }
        const next: NativeClerkSnapshot = await withTimeout(
          nativeAuth.signInWithOAuth(providerForStrategy(strategy)),
          90000,
          'Sign-in took too long to complete. Please try again.'
        );
        if (!next.isSignedIn) {
          setError('Sign-in completed, but Clerk did not create an active session. Please try again.');
          setBusy(null);
        }
        return;
      }

      const result = await clerk.client.signIn.create({
        strategy,
        redirectUrl: appUrl('/sso-callback'),
        actionCompleteRedirectUrl: appUrl('/home'),
        signUpIfMissing: true,
      });

      const url = result.firstFactorVerification?.externalVerificationRedirectURL;
      if (!url) {
        throw new Error('Clerk returned no OAuth URL — check that this provider is enabled in the Clerk dashboard.');
      }

      navigateToOAuthUrl(url.toString());
    } catch (err) {
      track('auth_failure', {
        source: providerForStrategy(strategy),
        outcome: isNative ? 'native' : 'web',
        errorCode: err instanceof Error ? err.name : 'unknown',
      });
      setError(errorMessage(err));
      setBusy(null);
    }
  };

  const submitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!isNative && !clerk?.client?.signIn) return;
    setError(null);
    setBusy('email');
    try {
      if (isNative) {
        if (!nativeAuth?.isAvailable) {
          throw new Error(nativeUnavailableMessage(nativeAuth));
        }
        await nativeAuth.signInWithPassword(email, password);
        return;
      }

      const signIn = clerk.client.signIn;
      const attempt = await signIn.create({
        strategy: 'password',
        identifier: email,
        password,
      });
      if (attempt.status === 'complete' && attempt.createdSessionId) {
        await clerk.setActive({ session: attempt.createdSessionId });
      } else {
        // For first-ship, we only support password sign-in. MFA / email-code
        // flows can be added later if Clerk's instance enables them.
        setError(
          `Sign-in incomplete (status=${attempt.status}). Try ${
            isNative ? 'Google or Apple' : 'Google'
          }.`
        );
      }
    } catch (err) {
      track('auth_failure', {
        source: 'email',
        outcome: isNative ? 'native' : 'web',
        errorCode: err instanceof Error ? err.name : 'unknown',
      });
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  if (!clerk?.loaded) {
    return (
      <div className="text-center text-sm" style={{ color: c.inkMuted, fontFamily: typ.body }}>
        Loading…
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-3">
      {isNative && (
        <Btn
          c={c}
          typ={typ}
          variant="ghost"
          disabled={!!busy}
          onClick={() => startOAuth('oauth_apple')}
          icon={
            // Filled Apple glyph (Apple's brand guidelines require the logo;
            // an SVG is more reliable than the U+F8FF glyph which only renders
            // on Apple-platform fonts). Inherits text color from the button.
            <svg
              aria-hidden
              width="14"
              height="17"
              viewBox="0 0 14 17"
              fill="currentColor"
              style={{ marginTop: -2 }}
            >
              <path d="M11.182 9.025c-.022-2.18 1.78-3.225 1.864-3.276-1.014-1.483-2.59-1.685-3.151-1.71-1.341-.135-2.617.79-3.297.79-.68 0-1.731-.77-2.847-.748C2.293 4.103.95 4.93.224 6.213-1.232 8.79-.143 12.585 1.273 14.667c.692 1.018 1.518 2.16 2.604 2.12 1.046-.04 1.44-.677 2.704-.677 1.263 0 1.617.677 2.722.655 1.124-.018 1.836-1.034 2.522-2.057.795-1.18 1.123-2.323 1.142-2.382-.025-.011-2.193-.842-2.215-3.301zM9.103 2.62C9.685 1.91 10.077.927 9.97 0c-.842.034-1.86.561-2.46 1.27-.539.628-1.012 1.63-.884 2.54.94.073 1.895-.477 2.477-1.19z" />
            </svg>
          }
        >
          {busy === 'apple' ? 'Connecting…' : 'Continue with Apple'}
        </Btn>
      )}

      <Btn
        c={c}
        typ={typ}
        variant="ghost"
        disabled={!!busy}
        onClick={() => startOAuth('oauth_google')}
        icon={
          // Google "G" mark in brand colors. Per Google's branding guidelines
          // for "Sign in with Google" buttons, the multi-color G is the
          // approved icon (the monochrome "G" we had before is a violation
          // of their identity guidelines: https://developers.google.com/identity/branding-guidelines).
          <svg aria-hidden width="16" height="16" viewBox="0 0 18 18">
            <path
              fill="#4285F4"
              d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
            />
            <path
              fill="#34A853"
              d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
            />
            <path
              fill="#FBBC05"
              d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
            />
            <path
              fill="#EA4335"
              d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
            />
          </svg>
        }
      >
        {busy === 'google' ? 'Connecting…' : 'Continue with Google'}
      </Btn>

      <div className="my-1 flex items-center gap-3" aria-hidden>
        <div className="h-px flex-1" style={{ background: c.line }} />
        <span
          className="text-[11px] uppercase tracking-[0.14em]"
          style={{ color: c.inkMuted, fontFamily: typ.body }}
        >
          or
        </span>
        <div className="h-px flex-1" style={{ background: c.line }} />
      </div>

      <form
        onSubmit={submitEmail}
        className="flex flex-col gap-2.5"
        data-testid="email-signin-form"
      >
        <label className="flex flex-col gap-1" style={{ fontFamily: typ.body }}>
          <span className="text-[12px] font-medium" style={{ color: c.inkSoft }}>
            Email
          </span>
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
            className="rounded-[12px] border px-3 py-2.5 text-[15px]"
            style={{ borderColor: c.line, background: c.surface, color: c.ink }}
            placeholder="you@example.com"
          />
        </label>
        <label className="flex flex-col gap-1" style={{ fontFamily: typ.body }}>
          <span className="text-[12px] font-medium" style={{ color: c.inkSoft }}>
            Password
          </span>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(ev) => setPassword(ev.target.value)}
            className="rounded-[12px] border px-3 py-2.5 text-[15px]"
            style={{ borderColor: c.line, background: c.surface, color: c.ink }}
            placeholder="••••••••"
          />
        </label>

        {error ? (
          <div
            role="alert"
            className="rounded-[10px] border px-3 py-2 text-[13px]"
            style={{
              borderColor: '#ff8b8b',
              background: '#ff8b8b22',
              color: c.ink,
              fontFamily: typ.body,
            }}
          >
            {error}
          </div>
        ) : null}

        <Btn c={c} typ={typ} variant="primary" type="submit" disabled={!!busy}>
          {busy === 'email' ? 'Signing in…' : 'Sign in'}
        </Btn>
      </form>

      <p
        className="mt-1 text-center text-[12px]"
        style={{ color: c.inkMuted, fontFamily: typ.body }}
      >
        {isNative ? (
          <>
            New to Chancey? Tap &ldquo;Continue with Apple&rdquo; or &ldquo;Continue with
            Google&rdquo; &mdash; we&apos;ll create your account on first sign-in.
          </>
        ) : (
          <>
            New to Chancey? Tap &ldquo;Continue with Google&rdquo; &mdash; we&apos;ll create
            your account on first sign-in.
          </>
        )}
      </p>
    </div>
  );
};
